import { useCallback } from 'react';
import { useAuthStore } from '@stores/authStore';
import { usePermissionStore } from '@stores/permissionStore';

// 权限检查钩子
export const usePermission = () => {
  const { user, isAuthenticated } = useAuthStore();
  const { hasPermission, hasRole, fetchPermissions, fetchRoles, fetchUserRoles } = usePermissionStore();

  // 初始化加载权限数据
  const initializePermissions = useCallback(async () => {
    if (isAuthenticated) {
      await Promise.all([
        fetchPermissions(),
        fetchRoles(),
        fetchUserRoles(),
      ]);
    }
  }, [isAuthenticated, fetchPermissions, fetchRoles, fetchUserRoles]);

  // 检查是否拥有指定权限
  const checkPermission = (permissionCode: string): boolean => {
    if (!isAuthenticated) return false;
    return hasPermission(permissionCode);
  };

  // 检查是否拥有指定角色
  const checkRole = (roleCode: string): boolean => {
    if (!isAuthenticated) return false;
    return hasRole(roleCode);
  };

  // 检查是否为管理员
  const isAdmin = (): boolean => {
    return checkRole('admin');
  };

  // 检查是否为项目经理
  const isProjectManager = (): boolean => {
    return checkRole('project_manager');
  };

  // 检查是否为开发人员
  const isDeveloper = (): boolean => {
    return checkRole('developer');
  };

  // 检查是否为查看者
  const isViewer = (): boolean => {
    return checkRole('viewer');
  };

  return {
    // 状态
    isAuthenticated,
    user,
    
    // 方法
    initializePermissions,
    checkPermission,
    checkRole,
    isAdmin,
    isProjectManager,
    isDeveloper,
    isViewer,
  };
};

// 权限控制的路由守卫
export const requirePermission = (permissionCode: string) => {
  return (_to: unknown, _from: unknown, next: (arg?: { path: string }) => void) => {
    const { isAuthenticated } = useAuthStore.getState();
    const { hasPermission } = usePermissionStore.getState();

    if (!isAuthenticated) {
      next({ path: '/login' });
    } else if (!hasPermission(permissionCode)) {
      next({ path: '/403' });
    } else {
      next();
    }
  };
};

// 角色控制的路由守卫
export const requireRole = (roleCode: string) => {
  return (_to: unknown, _from: unknown, next: (arg?: { path: string }) => void) => {
    const { isAuthenticated } = useAuthStore.getState();
    const { hasRole } = usePermissionStore.getState();

    if (!isAuthenticated) {
      next({ path: '/login' });
    } else if (!hasRole(roleCode)) {
      next({ path: '/403' });
    } else {
      next();
    }
  };
};